import { useMemo } from 'react'
import { Card, Title, Text, Group } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { formatDateForChart } from '@/shared/lib/utils'
import { CHART_COLORS, ChartGradient } from '@/shared/lib/chartColors.tsx'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { UsersSessionsHistory } from '@/shared/api/topli/TopliApi'

interface MAUChartProps {
  data: UsersSessionsHistory[]
  isLoading?: boolean
}

export const MAUChart = ({ data, isLoading }: MAUChartProps) => {
  const { t } = useTranslation(['dashboard'])
  const { language } = useLanguageStore()

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []

    const monthlyData: { [key: string]: number[] } = {}

    data.forEach(item => {
      const date = new Date(item.date)
      const monthKey = format(date, 'yyyy-MM')

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = []
      }
      monthlyData[monthKey].push(item.numberOfSession)
    })

    const sortedMonths = Object.keys(monthlyData).sort()

    const chartDataPoints = sortedMonths.map(monthKey => {
      const sessions = monthlyData[monthKey]
      const totalSessions = sessions.reduce((sum, session) => sum + session, 0)

      return {
        date: formatDateForChart(monthKey + '-01', language, { showDay: false }),
        value: totalSessions,
      }
    })

    return chartDataPoints
  }, [data, language])

  const lastMonthStats = useMemo(() => {
    if (!data || data.length === 0) return { total: 0, movingAverage: 0 }

    const last30 = data.slice(-30)

    const totalLast30Days = last30.reduce((sum, day) => sum + day.numberOfSession, 0)
    const movingAverage = totalLast30Days / 30

    return {
      total: totalLast30Days,
      movingAverage: Math.ceil(movingAverage),
    }
  }, [data])

  if (isLoading) {
    return (
      <Card shadow='sm' padding='lg' radius='md' className='rounded-[1.25rem]'>
        <Title order={3} mb='md'>
          {t('dashboard:mau_chart_title')}
        </Title>
        <div className='h-64 bg-gray-100 animate-pulse rounded'></div>
      </Card>
    )
  }

  return (
    <Card shadow='sm' padding='lg' radius='md' className='rounded-[1.25rem]'>
      <Title order={3} mb='md'>
        {t('dashboard:mau_chart_title')}
      </Title>

      <Group mb='lg'>
        <div>
          <Text size='lg' fw={700}>
            {lastMonthStats.total}
          </Text>
          <Text size='sm' c='dimmed'>
            {t('dashboard:total_users_30_days')}
          </Text>
        </div>
        <div>
          <Text size='lg' fw={700}>
            {lastMonthStats.movingAverage}
          </Text>
          <Text size='sm' c='dimmed'>
            {t('dashboard:moving_average_30_days')}
          </Text>
        </div>
      </Group>

      <div className='h-64'>
        <ResponsiveContainer width='100%' height='100%'>
          <AreaChart data={chartData}>
            <defs>
              <ChartGradient />
            </defs>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis
              dataKey='date'
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor='end'
              height={60}
            />
            <YAxis tick={{ fontSize: 12 }} allowDecimals={true} domain={[0, 'dataMax']} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '8px',
              }}
              formatter={(value: any) => [`${value}`, t('dashboard:total_users')]}
              labelFormatter={label => `${label}`}
            />
            <Area
              type='monotone'
              dataKey='value'
              stroke={CHART_COLORS.primary}
              strokeWidth={3}
              fill='url(#colorGradient)'
              fillOpacity={0.6}
              activeDot={{ r: 6 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}
